import CancelIcon from '@mui/icons-material/Cancel';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveIcon from '@mui/icons-material/Save';
import {
  Box,
  Button,
  CircularProgress,
  Drawer,
  IconButton,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { FC, memo, useEffect, useMemo, useState } from 'react';
import { ConfirmDialog } from 'src/components/ConfirmDialog';
import { ShipmentLegRunListCard } from 'src/components/shipment/ShipmentLegRunListCard';
import { ShipmentOrderLane } from 'src/entities/ShipmentOrderLane';
import { useQueryShipmentOrderLaneDetail } from 'src/hooks/useShipmentOrderLanes';
import { buildShipmentLegRunItems, ShipmentLegRunItem } from 'src/utils/shipmentLegRunItems';

import { useLaneDetailEdit } from '../hooks/useLaneDetailEdit';

import LaneDetailAssignment from './LaneDetailAssignment';
import LaneDetailBasicInfo from './LaneDetailBasicInfo';
import LaneDetailBerthInfo from './LaneDetailBerthInfo';

type Props = {
  selectedLane: ShipmentOrderLane | null;
  startOn: string;
  isOpen: boolean;
  onClose: () => void;
};

/**
 * レーン詳細シートコンポーネント
 * 右側からスライドして表示
 */
const LaneDetailSheet: FC<Props> = memo(({ selectedLane, startOn, isOpen, onClose }) => {
  const [tabValue, setTabValue] = useState(0);
  const { enqueueSnackbar } = useSnackbar();
  const [confirmDiaalogOpen, setConfirmDialogOpen] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setTabValue(0);
    }
  }, [isOpen]);

  // 詳細データ取得
  const {
    data: detail,
    isLoading,
    isError,
    error,
    refetch,
  } = useQueryShipmentOrderLaneDetail(selectedLane?.id ?? null);

  // エラー時のトースト通知
  useEffect(() => {
    if (isError && error) {
      enqueueSnackbar(
        `レーン詳細の取得に失敗しました: ${error.message || '不明なエラー'}`,
        { variant: 'error' }
      );
    }
  }, [isError, error, enqueueSnackbar]);

  // 編集状態管理
  const {
    isEditing,
    isSaving,
    requiredTruckCount,
    berthReservations,
    manualAssignments,
    modified,
    startEditing,
    cancelEditing,
    saveChanges,
    setRequiredTruckCount,
    updateBerthReservation,
    addBerthReservation,
    removeBerthReservation,
    updateManualAssignment,
    addManualAssignment,
    removeManualAssignment,
  } = useLaneDetailEdit(detail);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleClose = () => {
    if (modified) {
      setConfirmDialogOpen(true);
    } else {
      if (isEditing) {
        cancelEditing();
      }
      onClose();
    }
  };

  const handleConfirmOK = () => {
    setConfirmDialogOpen(false);
    onClose();
  };

  const handleConfirmCancel = () => {
    setConfirmDialogOpen(false);
  };

  const legRunItems: ShipmentLegRunItem[] = useMemo(() => {
    if (!detail) return [];

    return detail.shipmentOrders.flatMap((shipmentData) => buildShipmentLegRunItems(shipmentData));
  }, [detail]);

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={handleClose}
      sx={{
        '& .MuiDrawer-paper': {
          minWidth: '700px',
          width: '70vw',
        },
      }}
    >
      {/* ヘッダー */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
        }}
      >
        <Typography variant="h4">
          レーン詳細 {selectedLane && `#${selectedLane.id}`}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {detail && !isLoading && (
            <>
              {isEditing ? (
                <>
                  <Button
                    variant="contained"
                    startIcon={isSaving ? <CircularProgress size={16} /> : <SaveIcon />}
                    onClick={saveChanges}
                    disabled={isSaving}
                  >
                     {isSaving ? '保存中...' : '保存'}
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<CancelIcon />}
                    onClick={cancelEditing}
                    disabled={isSaving}
                  >
                    キャンセル
                  </Button>
                </>
              ) : (
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={startEditing}
                >
                  編集
                </Button>
              )}
            </>
          )}
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      {/* タブ */}
      <Box sx={{ p: 2, }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="配車管理" />
          <Tab label="バース情報" />
          <Tab label="LEG一覧" />
          <Tab label="基本情報" />
        </Tabs>
      </Box>

      {/* コンテンツ */}
      <Box sx={{ flexGrow: 1, overflow: 'auto', p: 2 }}>
        {isLoading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: 200,
            }}
          >
            <CircularProgress />
          </Box>
        ) : detail ? (
          <>
            {tabValue === 0 && (
              <LaneDetailAssignment
                detail={detail}
                isEditing={isEditing}
                requiredTruckCount={requiredTruckCount}
                manualAssignments={manualAssignments}
                onRequiredTruckCountChange={setRequiredTruckCount}
                onUpdateAssignment={updateManualAssignment}
                onAddAssignment={addManualAssignment}
                onRemoveAssignment={removeManualAssignment}
                setTabValue={setTabValue}
                startOn={startOn}
              />
            )}
            {tabValue === 1 && (
              <LaneDetailBerthInfo
                detail={detail}
                isEditing={isEditing}
                berthReservations={berthReservations}
                onUpdateReservation={updateBerthReservation}
                onAddReservation={addBerthReservation}
                onRemoveBerthReservation={removeBerthReservation}
              />
            )}
            {tabValue === 2 && <ShipmentLegRunListCard rows={legRunItems} />}
            {tabValue === 3 && <LaneDetailBasicInfo detail={detail} isEditing={isEditing} />}
          </>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
              py: 4,
            }}
          >
            <Typography color="text.secondary">
              データを取得できませんでした
            </Typography>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => {
                void refetch();
              }}
            >
              再試行
            </Button>
          </Box>
        )}
      </Box>
      <ConfirmDialog
        open={confirmDiaalogOpen}
        message="保存していない変更を破棄しますか？"
        handleOk={handleConfirmOK}
        handleCancel={handleConfirmCancel}
      />
    </Drawer>
  );
});

export default LaneDetailSheet;
